<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :moduleEnabler="moduleEnabler"
    :is-form-fetched="isFormFetched"
    :isCustomSaving="loading"
    :inputs="inputs"
    @save="save"
  >
    <template v-for="(input, idx) of inputs" #[`${input.name}.input`]="data">
      <v-col cols="3" :key="idx">
        <data-input
          v-model="selectedDataSource.settings[data.name]"
          v-bind="data"
          @change="valueChanged(data.name, $event)"
        />
      </v-col>
    </template>
    <template #layerId.input="data">
      <v-col cols="3">
        <data-input v-model="selectedDataSource.settings.layer_id" v-bind="data" @change="layerIdChanged" />
      </v-col>
    </template>
  </admin-wrapper>
</template>
<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
export default {
  name: 'OrdersSettings',
  components: { AdminWrapper, DataInput },
  mixins: [prepareAttributesFeatureCardMixin],
  computed: {
    additionalModuleName: get('admin/additionalModules@ORDERS_MODULE.verbose_name'),
    isModuleEnabled: get('admin/settingsValues@orders_module_enabled'),
    moduleEnabler() {
      return {
        name: 'orders_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle2', { suffix: this.additionalModuleName }),
      };
    },
    moduleDataSourceSettingsName() {
      return 'order_datasource';
    },

    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    layersArray() {
      return this.$_objectToArray(this.layers);
    },
    layersFiltered() {
      return this.layersArray.filter(
        layer => layer.type === 'features_layer' && layer.layer_scope === 'core' && !layer.geometry_type
      );
    },
    layersMapped() {
      return this.layersFiltered.map(layer => {
        const { id, name } = layer;
        return { value: id, text: name };
      });
    },

    selectedLayerMetadata() {
      return this.layers?.[this.selectedDataSource.settings.layer_id] || undefined;
    },
    selectedLayerFormSchema() {
      return this.selectedLayerMetadata?.form_schema?.elements || [];
    },
    selectedLayerDataSourceName() {
      return this.selectedLayerMetadata?.data_source_name || '';
    },
    selectedLayerDataSourceMetadata() {
      return this.dataSources?.[this.selectedLayerDataSourceName] || undefined;
    },
    selectedLayerDataSourceAttributesSchema() {
      return this.selectedLayerDataSourceMetadata?.attributes_schema?.attributes || [];
    },
    selectedLayerAttributesArray() {
      return this.$_flattenStructure(
        this.getForm(this.selectedLayerFormSchema, this.selectedLayerDataSourceAttributesSchema, {
          returnDictRelationInfo: true,
          withOriginalDataType: true,
        })
      ).map(attribute => {
        const { attribute: value, label, dataType, dataTypeOriginal, isDict, items } = attribute;
        return { value, text: label, dataType, dataTypeOriginal, isDict, items };
      });
    },
    selectedLayerStatusAttributeValues() {
      return this.selectedLayerAttributesArray.find(
        attribute => attribute.value === this.selectedDataSource.settings.status_attribute
      )?.items;
    },

    inputs() {
      return [
        {
          name: 'layerId',
          translationPath: 'admin.orders.ordersLayer',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.layersMapped,
          readonly: this.loading,
        },
        {
          name: 'name_attribute',
          translationPath: 'admin.orders.nameAttributeTitle',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.selectedLayerAttributesArray.filter(attribute => attribute.dataType.name === 'text'),
          disabled: !this.selectedLayerDataSourceName,
          readonly: this.loading,
        },
        {
          name: 'description_attribute',
          translationPath: 'admin.orders.descriptionAttributeTitle',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.selectedLayerAttributesArray.filter(attribute => attribute.dataType.name === 'text'),
          disabled: !this.selectedLayerDataSourceName,
          readonly: this.loading,
        },
        {
          name: 'assigned_attribute',
          translationPath: 'admin.orders.assignedAttributeTitle',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.selectedLayerAttributesArray.filter(attribute => attribute.dataTypeOriginal.name === 'integer'),
          disabled: !this.selectedLayerDataSourceName,
          readonly: this.loading,
        },
        {
          name: 'deadline_attribute',
          translationPath: 'admin.orders.deadlineAttributeTitle',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.selectedLayerAttributesArray.filter(attribute =>
            ['date', 'datetime'].includes(attribute.dataType.name)
          ),
          disabled: !this.selectedLayerDataSourceName,
          readonly: this.loading,
        },
        {
          name: 'status_attribute',
          translationPath: 'admin.orders.statusAttributeTitle',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.selectedLayerAttributesArray.filter(
            attribute => attribute.dataTypeOriginal.name === 'text' && attribute.isDict
          ),
          disabled: !this.selectedLayerDataSourceName,
          readonly: this.loading,
        },
        {
          name: 'status_open_values',
          translationPath: 'admin.orders.statusNewValues',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          multiple: true,
          items: this.selectedLayerStatusAttributeValues,
          disabled: !this.selectedLayerDataSourceName,
          readonly: this.loading,
        },
      ];
    },
  },
  data: () => ({
    isFormFetched: false,
    loading: false,
    selectedDataSource: {
      datasource: '',
      settings: {
        layer_id: null,
        name_attribute: '',
        description_attribute: '',
        assigned_attribute: '',
        deadline_attribute: '',
        status_attribute: '',
        status_open_values: '',
      },
    },
    selectedDataSourceChanged: false,
  }),
  methods: {
    getLayers: call('layers/getLayers'),
    getDataSources: call('layers/getLayersMetadata'),
    getDataSourceSettings: call('datasourceSettings/getDatasourceSettings'),
    editDataSourceSettings: call('datasourceSettings/editDatasourceSettings'),

    clearColumns() {
      Object.keys(this.selectedDataSource.settings).forEach(key => {
        if (key !== 'layer_id') this.selectedDataSource.settings[key] = '';
      });
      this.selectedDataSourceChanged = true;
    },
    layerIdChanged(value) {
      this.clearColumns();
      if (!value && value !== 0) return;
      this.selectedDataSource.datasource = this.dataSources?.[this.selectedLayerDataSourceName]?.name || '';
    },
    valueChanged(name) {
      if (name === 'status_attribute') {
        this.selectedDataSource.settings['status_open_values'] = '';
      }
      this.selectedDataSourceChanged = true;
    },

    async init() {
      await Promise.all([
        this.getDataSources({ with_attributes_schema: true }),
        this.getLayers(),
        ...(this.isModuleEnabled ? [this.updateSelectedDataSource()] : []),
      ]);
      this.isFormFetched = true;
    },
    async updateSelectedDataSource() {
      try {
        const r = await this.getDataSourceSettings(this.moduleDataSourceSettingsName);
        const { datasource, settings } = r;
        this.selectedDataSource = {
          datasource,
          settings,
        };
      } catch {
        this.selectedDataSource = {
          settings: {
            layer_id: null,
            name_attribute: '',
            description_attribute: '',
            assigned_attribute: '',
            deadline_attribute: '',
            status_attribute: '',
            status_open_values: '',
          },
          datasource: '',
        };
      } finally {
        this.selectedDataSourceChanged = false;
      }
    },
    async save() {
      if (!this.selectedDataSourceChanged) return;
      this.loading = true;
      try {
        const payload = { name: this.moduleDataSourceSettingsName, settings: this.selectedDataSource };
        await this.editDataSourceSettings(payload);
        this.selectedDataSourceChanged = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) this.init();
    },
  },
  created() {
    if (this.isModuleEnabled) this.init();
    else this.isFormFetched = true;
  },
};
</script>
